import { useTranslation } from 'react-i18next';
import type { FileRejection } from 'react-dropzone';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { FileDropzone } from '@/shared/ui/FileDropzone';
import {
  IMPORT_ACCEPT,
  importMimeTypeOf,
  MAX_DOCUMENT_IMPORT_BYTES,
  MAX_IMPORT_BYTES,
  PDF_MIME_TYPE,
  SCHEMA_IMPORT_ACCEPT,
} from '../api/taxonomy-import.api';

interface ImportFilePickerProps {
  file: File | null;
  /** Why the last pick was refused, or the server's refusal of the import. */
  error: string | null;
  onPick: (file: File | null, error: string | null) => void;
  /** Also takes a PDF of naming rules, as a schema import does. */
  documents?: boolean;
  disabled?: boolean;
}

/**
 * The file an import reads. A spreadsheet is parsed here in full, so it is held to 10 MB; a PDF is
 * read by the model, which takes up to 20 MB.
 */
export function ImportFilePicker({
  file,
  error,
  onPick,
  documents = false,
  disabled,
}: ImportFilePickerProps) {
  const { t } = useTranslation('knowledge');

  const tooLarge = (forDocument: boolean) =>
    t(forDocument ? 'taxonomy.import.tooLargeDocument' : 'taxonomy.import.tooLarge');

  function handleDrop(accepted: File[], rejections: FileRejection[]) {
    const picked = accepted.at(0);
    if (!picked) {
      const overSize = rejections.some((r) => r.errors.some((e) => e.code === 'file-too-large'));
      onPick(
        null,
        overSize
          ? tooLarge(documents)
          : t(documents ? 'taxonomy.import.wrongTypeDocument' : 'taxonomy.import.wrongType'),
      );
      return;
    }
    // The dropzone allows the larger cap when it takes documents; a spreadsheet keeps its own.
    const isDocument = importMimeTypeOf(picked.name) === PDF_MIME_TYPE;
    if (picked.size > (isDocument ? MAX_DOCUMENT_IMPORT_BYTES : MAX_IMPORT_BYTES)) {
      onPick(null, tooLarge(isDocument));
    } else {
      onPick(picked, null);
    }
  }

  const removeLabel = file ? t('taxonomy.import.removeFile', { name: file.name }) : '';

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-xs">
        <FileDropzone
          accept={documents ? SCHEMA_IMPORT_ACCEPT : IMPORT_ACCEPT}
          maxSize={documents ? MAX_DOCUMENT_IMPORT_BYTES : MAX_IMPORT_BYTES}
          disabled={disabled}
          title={
            documents ? t('taxonomy.import.dropTitleDocument') : t('taxonomy.import.dropTitle')
          }
          caption={
            documents ? t('taxonomy.import.dropCaptionDocument') : t('taxonomy.import.dropCaption')
          }
          buttonLabel={t('taxonomy.import.dropButton')}
          onDrop={handleDrop}
        />
        {file && !error && (
          <div className="flex items-center gap-2xs">
            <p className="text-sm text-muted-foreground">
              {t('taxonomy.import.chosenFile', { name: file.name })}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-mini"
                  aria-label={removeLabel}
                  disabled={disabled}
                  onClick={() => onPick(null, null)}
                >
                  <XIcon className="size-3.5" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{removeLabel}</TooltipContent>
            </Tooltip>
          </div>
        )}
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    </TooltipProvider>
  );
}
